/*
  LadyCloshop — compra rápida desde las tarjetas de producto.

  El «+» de la tarjeta ([data-lc-quick]) abre la MISMA hoja de compra de la ficha: se pide la
  ficha en segundo plano, se toma su grupo de compra (talla, color, packs de Kaching y botones)
  y se muestra como panel que sube desde abajo. Así se mantienen los packs y los regalos.

  - Se precarga la ficha al pasar el ratón, al tocar o al enfocar el «+».
  - Los estilos y scripts de la ficha se cargan solo la primera vez que se abre.
  - Kaching se vuelve a iniciar con los datos del producto abierto.
  - Idéntica a la hoja de «Añadir a la cesta» de la ficha (solo ese botón; el contra reembolso
    sigue en la cesta).
  - Sin opciones ni packs: directo a la cesta. Si algo falla, se va a la ficha.
*/
if (!customElements.get('lc-quick-buy')) {
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  // Piezas de la ficha que no hacen falta en la hoja
  const SKIP_SCRIPTS = /(magnify|theme-editor|product-modal|media-gallery|lc-gallery-dots|pickup-availability|quick-add|lc-buy)\.js/;
  const pages = new Map();

  const getPage = (url) => {
    if (!pages.has(url)) {
      const request = fetch(url, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .catch((error) => {
          pages.delete(url);
          throw error;
        });
      pages.set(url, request);
    }
    return pages.get(url);
  };

  const loadAsset = (el) =>
    new Promise((resolve) => {
      if (el.tagName === 'LINK') {
        const href = el.getAttribute('href');
        if (document.querySelector(`link[href="${href}"]`)) return resolve();
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.onload = link.onerror = () => resolve();
        document.head.appendChild(link);
        return;
      }
      const src = el.getAttribute('src');
      if (SKIP_SCRIPTS.test(src) || document.querySelector(`script[src="${src}"]`)) return resolve();
      const script = document.createElement('script');
      script.src = src;
      script.async = false;
      script.onload = script.onerror = () => resolve();
      document.head.appendChild(script);
    });

  class LcQuickBuy extends HTMLElement {
    connectedCallback() {
      this.host = this.querySelector('[data-lc-quick-host]');
      this.backdrop = this.querySelector('[data-lc-quick-backdrop]');
      this.onKeydown = this.onKeydown.bind(this);

      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-lc-quick]');
        if (!trigger) return;
        event.preventDefault();
        event.stopPropagation();
        this.open(trigger);
      });
      const prefetch = (event) => {
        const trigger = event.target.closest?.('[data-lc-quick]');
        if (trigger) getPage(trigger.dataset.lcQuick).catch(() => {});
      };
      document.addEventListener('pointerenter', prefetch, { capture: true, passive: true });
      document.addEventListener('touchstart', prefetch, { capture: true, passive: true });
      document.addEventListener('focusin', prefetch);

      this.backdrop.addEventListener('click', () => this.close());

      // La ficha pinta de nuevo partes al cambiar de talla: mismos cambios que al abrir
      this.addEventListener('product-info:loaded', ({ target }) => {
        target.addPreProcessCallback?.((html) => this.clean(html));
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event?.source !== 'product-form') return;
          if (this.direct) {
            this.direct = false;
            this.host.innerHTML = '';
            this.group = null;
            return;
          }
          if (!this.isOpen) return;
          this.close({ restoreFocus: false });
        });
        // Si la compra directa falla (sin stock…), se abre la hoja para ver el aviso
        subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (!this.direct || event?.source !== 'product-form') return;
          this.direct = false;
          this.show();
        });
      }
    }

    get isOpen() {
      return !!this.group?.classList.contains('is-open');
    }

    async open(trigger) {
      if (this.busy) return;
      const url = trigger.dataset.lcQuick;
      this.busy = true;
      this.trigger = trigger;
      trigger.classList.add('is-loading');
      trigger.setAttribute('aria-busy', 'true');
      try {
        const html = await getPage(url);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const info = doc.querySelector('product-info');
        if (!info || !info.querySelector('[data-lc-buy-group]')) throw new Error('sin grupo de compra');

        // Estilos y scripts de la ficha (solo los que falten en esta página)
        const assets = [...info.querySelectorAll('link[rel="stylesheet"], script[src]')];
        assets.forEach((el) => el.remove());
        await Promise.all(assets.map(loadAsset));

        this.prepare(info);
        this.render(doc, info);

        // Nada que elegir (sin tallas ni colores y sin packs): a la cesta directamente
        const options = this.host.querySelectorAll('variant-selects fieldset, variant-selects select');
        const packs = this.host.querySelector('script.kaching-bundles-deal-block-settings:not(:empty)');
        const submit = this.group.querySelector('.product-form__submit');
        if (!options.length && !packs && submit && !submit.disabled) {
          this.direct = true;
          submit.click();
          return;
        }
        this.show();
      } catch (error) {
        console.error(error);
        window.location.href = url;
      } finally {
        this.busy = false;
        trigger.classList.remove('is-loading');
        trigger.removeAttribute('aria-busy');
      }
    }

    // Ids únicos (la ficha usa el id de su sección), sin cambiar la dirección al elegir talla
    prepare(info) {
      const sectionId = info.dataset.section;
      const newId = `lcq-${sectionId}`;
      info.innerHTML = info.innerHTML.replaceAll(sectionId, newId);
      [...info.attributes].forEach((attr) => {
        if (attr.value.includes(sectionId)) info.setAttribute(attr.name, attr.value.replaceAll(sectionId, newId));
      });
      info.dataset.originalSection = sectionId;
      info.dataset.updateUrl = 'false';
      this.clean(info);
    }

    clean(root) {
      root
        .querySelectorAll('.product__media-wrapper, lc-buy-panel, [data-lc-backdrop], .lc-pdp-below, product-modal, modal-dialog, pickup-availability, product-recommendations')
        .forEach((el) => el.remove());
      // Las animaciones de entrada (transform) romperían el panel fijo
      root.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.remove('scroll-trigger', 'animate--slide-in', 'animate--fade-in'));
    }

    render(doc, info) {
      // Datos de los packs de este producto (Kaching los lee del documento)
      const kaching = [...doc.querySelectorAll('script.kaching-bundles-product, script.kaching-bundles-deal-block-settings')]
        .map((s) => s.outerHTML)
        .join('');
      HTMLUpdateUtility.setInnerHTML(this.host, kaching + info.outerHTML);
      document.documentElement.classList.remove('lc-kaching-off');

      this.group = this.host.querySelector('[data-lc-buy-group]');
      this.group.classList.add('lc-buy-group--sheet', 'lc-quick__sheet');
      // Idéntica a la hoja de «Añadir a la cesta» de la ficha
      this.group.dataset.intent = 'cart';
      this.group.setAttribute('role', 'dialog');
      this.group.setAttribute('aria-modal', 'true');
      if (!this.group.hasAttribute('aria-labelledby')) {
        this.group.setAttribute('aria-label', doc.querySelector('.product__title h1')?.textContent.trim() || '');
      }
      this.group.querySelector('[data-lc-close]')?.addEventListener('click', () => this.close());

      // Packs (textos de la marca) y Kaching
      document.dispatchEvent(new CustomEvent('shopify:section:load', { detail: { sectionId: info.dataset.section } }));
      this.startKaching();
      window.Shopify?.PaymentButton?.init?.();
      // Si Kaching no llega a pintar los packs, vuelve el selector de tallas del tema
      clearTimeout(this.kachingTimer);
      if (this.host.querySelector('script.kaching-bundles-deal-block-settings:not(:empty)')) {
        this.kachingTimer = setTimeout(() => {
          if (this.group && !this.group.querySelector('kaching-bundles-block .kaching-bundles__bar')) {
            document.documentElement.classList.add('lc-kaching-off');
          }
        }, 6000);
      }
    }

    // Kaching monta los packs al iniciarse; si tarda (su script aún cargando), se reintenta
    startKaching() {
      const group = this.group;
      const hasDeal = this.host.querySelector('script.kaching-bundles-deal-block-settings:not(:empty)');
      let tries = 0;
      const run = () => {
        if (this.group !== group) return;
        try {
          window.kachingBundlesInitialize?.();
        } catch (error) {
          console.error(error);
        }
        tries += 1;
        if (hasDeal && tries < 6) {
          setTimeout(() => {
            if (this.group !== group || group.querySelector('kaching-bundles-block')) return;
            // Kaching pinta en el primer formulario de producto de la página: dentro de una ficha
            // (p. ej. «Completa tu look») ese es el del producto principal. Se le marca el sitio
            // de la hoja con su marcador y su vigilante lo monta con el producto de la hoja.
            this.addKachingPlaceholder(group);
            run();
          }, 800);
        }
      };
      run();
    }

    addKachingPlaceholder(group) {
      if (group.querySelector('kaching-bundle')) return;
      const form = group.querySelector('product-form');
      const data = this.host.querySelector('script.kaching-bundles-product');
      if (!form || !data) return;
      let id;
      try {
        id = JSON.parse(data.textContent).id;
      } catch (error) {
        return;
      }
      const placeholder = document.createElement('kaching-bundle');
      placeholder.setAttribute('product-id', id);
      form.before(placeholder);
    }

    show() {
      // Se cierra también deslizando hacia abajo (lc-sheet-drag.js)
      window.lcSheetDrag?.(this.group, () => this.close());
      this.backdrop.hidden = false;
      document.documentElement.classList.add('lc-buy-lock');
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          this.group.classList.add('is-open');
          this.backdrop.classList.add('is-open');
        })
      );
      document.addEventListener('keydown', this.onKeydown);
      setTimeout(() => this.group.querySelector('[data-lc-close]')?.focus({ preventScroll: true }), REDUCE.matches ? 0 : 80);
    }

    close({ restoreFocus = true } = {}) {
      if (!this.isOpen) return;
      const group = this.group;
      group.classList.remove('is-open');
      this.backdrop.classList.remove('is-open');
      document.documentElement.classList.remove('lc-buy-lock');
      document.removeEventListener('keydown', this.onKeydown);
      clearTimeout(this.kachingTimer);
      setTimeout(
        () => {
          if (this.group !== group || this.isOpen) return;
          this.backdrop.hidden = true;
          this.host.innerHTML = '';
          this.group = null;
        },
        REDUCE.matches ? 0 : 420
      );
      if (restoreFocus) this.trigger?.focus({ preventScroll: true });
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.group) return;
      const items = [...this.group.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('lc-quick-buy', LcQuickBuy);
}
